'use client'

import Link from 'next/link'
import { useState, useMemo } from 'react'
import { useSession } from 'next-auth/react'
import { FglHeader } from '@/components/layout/fgl-header'
import { useTranslation } from '@/i18n/client'
import { LenisProvider, Reveal, GliderBackdrop } from '@/components/award'
import {
  Users, Globe, Shield, Zap, BookOpen,
  Thermometer, Play, Smartphone,
  Database, Award, TrendingUp, MapPin, FileText,
  BarChart, Compass, Star, Activity,
  Crown, Rocket, ChevronRight, MessageSquare,
  CloudSun, Mail, Image as ImageIcon, Bug, Wrench, Trophy,
  ShoppingBag, BatteryCharging, X, UserPlus, LogIn, ArrowRight,
  Bot, Podcast as PodcastIcon, Newspaper, Route, Layers,
  RadioTower, Bell, KeyRound, GitPullRequest, PieChart,
  Radar, PlaneTakeoff, LineChart, FileJson, FileDown,
  Sparkles, Mountain, HeartPulse, Vote, Fingerprint, Info
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

type Tier = 'public' | 'start' | 'pro' | 'premium'

/**
 * Ein Feature verweist auf i18n-Keys statt hartcodierter Strings.
 * baseKey ist der Prefix in de.json unter features.v2.<tier>.<slug>
 * Der Renderer baut daraus title/short/long/b1..b4.
 */
interface Feature {
  id: string
  icon: LucideIcon
  baseKey: string // z.B. 'features.v2.pub.wertung'
  targetLink: string
  isNew?: boolean
  /** Coming-Soon-Marker: "Herbst 2026", "2027", etc. */
  comingSoon?: string
}

interface TierData {
  key: Tier
  tierKey: string // 'features.v2.tiers.public' etc.
  accentColor: string
  accentBg: string
  borderColor: string
  badgeText: string
  features: Feature[]
}

// ═══════════════════════════════════════════════════════════════
// PUBLIC FEATURES
// ═══════════════════════════════════════════════════════════════

const PUBLIC_FEATURES: Feature[] = [
  { id: 'wertung', icon: Trophy, baseKey: 'features.v2.pub.wertung', targetLink: '/wertung' },
  { id: 'wetter', icon: CloudSun, baseKey: 'features.v2.pub.wetter', targetLink: '/weather' },
  { id: 'flugplaetze', icon: MapPin, baseKey: 'features.v2.pub.flugplaetze', targetLink: '/flying-fields' },
  { id: 'luftraum3d', icon: Layers, baseKey: 'features.v2.pub.luftraum3d', targetLink: '/airspace-3d' },
  { id: 'news', icon: Newspaper, baseKey: 'features.v2.pub.news', targetLink: '/news' },
  { id: 'podcast', icon: PodcastIcon, baseKey: 'features.v2.pub.podcast', targetLink: '/podcast' },
  { id: 'ansprech', icon: Users, baseKey: 'features.v2.pub.ansprech', targetLink: '/contacts' },
  { id: 'flugDetail', icon: PlaneTakeoff, baseKey: 'features.v2.pub.flugDetail', targetLink: '/wertung' },
  { id: 'cirra', icon: Bot, baseKey: 'features.v2.pub.cirra', targetLink: '/' },
  { id: 'galerie', icon: ImageIcon, baseKey: 'features.v2.pub.galerie', targetLink: '/gallery' },
  { id: 'faq', icon: BookOpen, baseKey: 'features.v2.pub.faq', targetLink: '/faq' },
  { id: 'akkuPreview', icon: BatteryCharging, baseKey: 'features.v2.pub.akkuPreview', targetLink: '/login' },
]

// ═══════════════════════════════════════════════════════════════
// START FEATURES
// ═══════════════════════════════════════════════════════════════

const START_FEATURES: Feature[] = [
  { id: 'igcUpload', icon: Zap, baseKey: 'features.v2.start.igcUpload', targetLink: '/app/upload' },
  { id: 'igcUploaderApp', icon: Smartphone, baseKey: 'features.v2.start.igcUploaderApp', targetLink: '/downloads', comingSoon: 'autumn2026' },
  { id: 'syncBridge', icon: GitPullRequest, baseKey: 'features.v2.start.syncBridge', targetLink: '/sync', isNew: true },
  { id: 'eigeneFluege', icon: Compass, baseKey: 'features.v2.start.eigeneFluege', targetLink: '/app/flights' },
  { id: 'hangar', icon: Database, baseKey: 'features.v2.start.hangar', targetLink: '/app/models', isNew: true },
  { id: 'setupPool', icon: FileText, baseKey: 'features.v2.start.setupPool', targetLink: '/app/settings/files', isNew: true },
  { id: 'akkuVoll', icon: BatteryCharging, baseKey: 'features.v2.start.akkuVoll', targetLink: '/app/batteries', isNew: true },
  { id: 'akkuKombis', icon: Layers, baseKey: 'features.v2.start.akkuKombis', targetLink: '/app/batteries', isNew: true },
  { id: 'setupKopplung', icon: Wrench, baseKey: 'features.v2.start.setupKopplung', targetLink: '/app/models', isNew: true },
  { id: 'modellDb', icon: Compass, baseKey: 'features.v2.start.modellDb', targetLink: '/app/models' },
  { id: 'pilotProfil', icon: Fingerprint, baseKey: 'features.v2.start.pilotProfil', targetLink: '/app/settings' },
  { id: 'community', icon: Users, baseKey: 'features.v2.start.community', targetLink: '/app' },
  { id: 'nachrichten', icon: MessageSquare, baseKey: 'features.v2.start.nachrichten', targetLink: '/app/messages' },
  { id: 'newsletter', icon: Mail, baseKey: 'features.v2.start.newsletter', targetLink: '/app/settings' },
  { id: 'push', icon: Bell, baseKey: 'features.v2.start.push', targetLink: '/app/settings' },
  { id: 'freegliddy', icon: Bot, baseKey: 'features.v2.start.freegliddy', targetLink: '/app' },
  { id: 'marketplace', icon: ShoppingBag, baseKey: 'features.v2.start.marketplace', targetLink: '/app/community?channel=marketplace' },
  { id: 'twofa', icon: KeyRound, baseKey: 'features.v2.start.twofa', targetLink: '/app/settings' },
  { id: 'bugReports', icon: Bug, baseKey: 'features.v2.start.bugReports', targetLink: '/app' },
  { id: 'badges', icon: Award, baseKey: 'features.v2.start.badges', targetLink: '/app/badges' },
  { id: 'verein', icon: HeartPulse, baseKey: 'features.v2.start.verein', targetLink: '/app/club', comingSoon: 'year2027' },
]

// ═══════════════════════════════════════════════════════════════
// PRO FEATURES
// ═══════════════════════════════════════════════════════════════

const PRO_FEATURES: Feature[] = [
  { id: 'cesium3d', icon: Mountain, baseKey: 'features.v2.pro.cesium3d', targetLink: '/app/flights' },
  { id: 'polaren', icon: LineChart, baseKey: 'features.v2.pro.polaren', targetLink: '/app/flights' },
  { id: 'polar5', icon: PieChart, baseKey: 'features.v2.pro.polar5', targetLink: '/app/flights' },
  { id: 'logbuch', icon: BookOpen, baseKey: 'features.v2.pro.logbuch', targetLink: '/app/logbook' },
  { id: 'kiFlug', icon: Bot, baseKey: 'features.v2.pro.kiFlug', targetLink: '/app/flights' },
  { id: 'kiWetter', icon: CloudSun, baseKey: 'features.v2.pro.kiWetter', targetLink: '/app/flights' },
  { id: 'mcSollfahrt', icon: TrendingUp, baseKey: 'features.v2.pro.mcSollfahrt', targetLink: '/app/flights' },
]

// ═══════════════════════════════════════════════════════════════
// PREMIUM FEATURES
// ═══════════════════════════════════════════════════════════════

const PREMIUM_FEATURES: Feature[] = [
  { id: 'thermikAtlas', icon: Thermometer, baseKey: 'features.v2.premium.thermikAtlas', targetLink: '/app/thermal-atlas' },
  { id: 'ausloesepunkt', icon: Radar, baseKey: 'features.v2.premium.ausloesepunkt', targetLink: '/app/thermal-atlas' },
  { id: 'thermikVorhersage', icon: Sparkles, baseKey: 'features.v2.premium.thermikVorhersage', targetLink: '/app/thermal-atlas' },
  { id: 'thermikPdf', icon: FileDown, baseKey: 'features.v2.premium.thermikPdf', targetLink: '/app/thermal-atlas' },
  { id: 'polarJson', icon: FileJson, baseKey: 'features.v2.premium.polarJson', targetLink: '/app/models' },
  { id: 'kml', icon: Route, baseKey: 'features.v2.premium.kml', targetLink: '/app/flights' },
  { id: 'wertungDeep', icon: Vote, baseKey: 'features.v2.premium.wertungDeep', targetLink: '/app/flights' },
  { id: 'triangle', icon: Route, baseKey: 'features.v2.premium.triangle', targetLink: '/app/flights' },
  { id: 'csv', icon: FileDown, baseKey: 'features.v2.premium.csv', targetLink: '/app/flights' },
  { id: 'pdfBesch', icon: FileDown, baseKey: 'features.v2.premium.pdfBesch', targetLink: '/app/my-rankings' },
  { id: 'heatmap', icon: Activity, baseKey: 'features.v2.premium.heatmap', targetLink: '/heatmap' },
  { id: 'kiPriority', icon: Crown, baseKey: 'features.v2.premium.kiPriority', targetLink: '/app' },
]

const TIERS: TierData[] = [
  {
    key: 'public',
    tierKey: 'features.v2.tiers.public',
    // r19.55.193: theme-aware via CSS-Vars in globals.css.
    // Hell: dunkles Grau #4b5563 · Dunkel: helles Grau #d1d5db.
    accentColor: 'var(--tier-public-color)',
    accentBg: 'var(--tier-public-bg)',
    borderColor: 'var(--tier-public-border)',
    badgeText: 'var(--tier-public-color)',
    features: PUBLIC_FEATURES,
  },
  {
    key: 'start',
    tierKey: 'features.v2.tiers.start',
    // r19.55.193: theme-aware. Hell: tiefes Petrol #0f5470 · Dunkel: helles Sky #7dd3fc.
    accentColor: 'var(--tier-start-color)',
    accentBg: 'var(--tier-start-bg)',
    borderColor: 'var(--tier-start-border)',
    badgeText: 'var(--tier-start-color)',
    features: START_FEATURES,
  },
  {
    key: 'pro',
    tierKey: 'features.v2.tiers.pro',
    accentColor: '#1d4ed8',
    accentBg: 'rgba(88,166,255,0.08)',
    borderColor: 'rgba(88,166,255,0.3)',
    badgeText: '#1d4ed8',
    features: PRO_FEATURES,
  },
  {
    key: 'premium',
    tierKey: 'features.v2.tiers.premium',
    accentColor: '#f2c94e',
    accentBg: 'rgba(242,201,78,0.08)',
    borderColor: 'rgba(242,201,78,0.3)',
    badgeText: '#f2c94e',
    features: PREMIUM_FEATURES,
  },
]

// ═══════════════════════════════════════════════════════════════
// MODAL
// ═══════════════════════════════════════════════════════════════

interface ModalState {
  feature: Feature
  tier: TierData
}

function FeatureModal({
  state,
  onClose,
  isLoggedIn,
  t,
}: {
  state: ModalState
  onClose: () => void
  isLoggedIn: boolean
  t: (key: string, params?: Record<string, string | number>) => string
}) {
  const { feature, tier } = state
  const Icon = feature.icon
  const base = feature.baseKey

  const unlockText =
    tier.key === 'public' ? t('features.v2.modal.unlockPublic')
    : tier.key === 'start' ? t('features.v2.modal.unlockStart')
    : tier.key === 'pro' ? t('features.v2.modal.unlockPro')
    : t('features.v2.modal.unlockPremium')

  return (
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        className="wertung-modal-glass relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[24px] p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
        style={{ border: `2px solid ${tier.borderColor}` }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center transition hover:bg-white/10"
          aria-label={t('features.v2.modal.closeAria')}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-4 mb-5 pr-10">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ background: tier.accentBg, border: `1.5px solid ${tier.borderColor}` }}
          >
            <Icon className="w-7 h-7" style={{ color: tier.accentColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span
                className="wertung-flight-pill px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest"
                style={{ ['--pill-color' as any]: tier.badgeText }}
              >
                {t('features.v2.tierPill', { tier: t(`${tier.tierKey}.name`), badge: t(`${tier.tierKey}.badge`) })}
              </span>
              {feature.isNew && !feature.comingSoon && (
                <span
                  className="wertung-flight-pill px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest"
                  style={{ ['--pill-color' as any]: '#f2c94e' }}
                >
                  {t('features.v2.modal.newBadge')}
                </span>
              )}
              {feature.comingSoon && (
                <span
                  className="wertung-flight-pill px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest"
                  style={{ ['--pill-color' as any]: '#7e22ce' }}
                >
                  {t('features.v2.modal.soonBadge', { when: t(`features.v2.times.${feature.comingSoon}`) })}
                </span>
              )}
            </div>
            <h2 className="text-xl md:text-2xl font-black uppercase leading-tight">{t(`${base}.title`)}</h2>
          </div>
        </div>

        <p className="text-sm md:text-base leading-relaxed mb-5" style={{ color: 'var(--award-cirrus-2)' }}>
          {t(`${base}.long`)}
        </p>

        <div className="mb-6">
          <div className="award-eyebrow mb-3">{t('features.v2.modal.benefitsLabel')}</div>
          <ul className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: tier.accentColor }} />
                <span>{t(`${base}.b${i}`)}</span>
              </li>
            ))}
          </ul>
        </div>

        {feature.comingSoon ? (
          <div
            className="rounded-xl p-4 mb-6 text-sm"
            style={{ background: 'rgba(126,34,206,0.10)', border: '1px solid rgba(126,34,206,0.35)' }}
          >
            <div className="flex items-center gap-2 mb-1 font-bold" style={{ color: '#a855f7' }}>
              <Sparkles className="w-4 h-4" />
              <span>{t('features.v2.modal.soonBadge', { when: t(`features.v2.times.${feature.comingSoon}`) })}</span>
            </div>
            <p className="text-[13px]" style={{ color: 'var(--muted)' }}>
              {t('features.v2.modal.soonHint', { when: t(`features.v2.times.${feature.comingSoon}`) })}
            </p>
          </div>
        ) : (
          <div
            className="rounded-xl p-4 mb-6 text-sm"
            style={{ background: tier.accentBg, border: `1px solid ${tier.borderColor}` }}
          >
            <div className="flex items-center gap-2 mb-1 font-bold" style={{ color: tier.accentColor }}>
              <Rocket className="w-4 h-4" />
              <span>{t('features.v2.modal.howToUnlock')}</span>
            </div>
            <p className="text-[13px]" style={{ color: 'var(--muted)' }}>{unlockText}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          {isLoggedIn ? (
            <Link
              href={feature.targetLink}
              className="wertung-tab-pill flex-1 px-5 py-3 rounded-full text-[13px] font-mono font-bold uppercase tracking-[0.08em] flex items-center justify-center gap-2"
              data-state="active"
            >
              {t('features.v2.modal.ctaFeature')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          ) : (
            <>
              <Link
                href="/register"
                className="wertung-tab-pill flex-1 px-5 py-3 rounded-full text-[13px] font-mono font-bold uppercase tracking-[0.08em] flex items-center justify-center gap-2"
                data-state="active"
              >
                <UserPlus className="w-4 h-4" />
                {t('features.v2.modal.ctaRegister')}
              </Link>
              <Link
                href="/login"
                className="wertung-tab-pill flex-1 px-5 py-3 rounded-full text-[13px] font-mono font-bold uppercase tracking-[0.08em] flex items-center justify-center gap-2"
                data-state="inactive"
              >
                <LogIn className="w-4 h-4" />
                {t('features.v2.modal.ctaLogin')}
              </Link>
            </>
          )}
        </div>

        <p className="text-[11px] text-center mt-4" style={{ color: 'var(--muted)' }}>
          {t('features.v2.modal.footnote')}
        </p>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// HAUPT-SEITE
// ═══════════════════════════════════════════════════════════════

export default function FeaturesPage() {
  const { data: session } = useSession()
  const { t } = useTranslation()
  const [modalState, setModalState] = useState<ModalState | null>(null)

  const isLoggedIn = !!session?.user
  const totalFeatures = useMemo(
    () => TIERS.reduce((sum, tier) => sum + tier.features.length, 0),
    []
  )

  return (
    <LenisProvider>
      <div data-award="true" className="min-h-screen relative overflow-x-clip" style={{ background: 'var(--bg-primary)' }}>
        <GliderBackdrop />
        <FglHeader />

        <main className="relative z-10 max-w-[1320px] mx-auto px-4 pt-[100px] md:pt-[120px] pb-12">
          {/* HERO */}
          <section className="relative pb-10 md:pb-14">
            <div className="relative z-10">
              <Reveal>
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="award-eyebrow">{t('features.v2.hero.eyebrow')}</span>
                  <span className="h-px w-12" style={{ background: 'var(--award-line-hi)' }} />
                  <span className="award-label">{t('features.v2.hero.totalLabel', { count: totalFeatures })}</span>
                </div>
              </Reveal>

              <div className="grid grid-cols-12 gap-4 md:gap-10 items-end">
                <div className="col-span-12 md:col-span-7">
                  <Reveal delay={1}>
                    <h1 className="award-h-mega" style={{ marginBottom: 0 }}>
                      {t('features.v2.hero.title1')} <span className="award-grad-text">{t('features.v2.hero.titleAccent')}</span>
                    </h1>
                  </Reveal>
                </div>
                <div className="col-span-12 md:col-span-5">
                  <Reveal delay={2}>
                    <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--text-muted, var(--muted))' }}>
                      {t('features.v2.hero.subtitle')}
                    </p>
                  </Reveal>
                </div>
              </div>
            </div>
          </section>

          {/* Info-Bar */}
          <Reveal delay={2}>
            <div
              className="rounded-[16px] p-4 mb-8 flex items-start gap-3"
              style={{
                background: 'color-mix(in oklch, var(--award-ink-2) 88%, transparent)',
                border: '1px solid rgba(34,197,94,0.25)',
              }}
            >
              <Info className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#15803d' }} />
              <div className="text-sm leading-relaxed">
                <strong style={{ color: '#15803d' }}>{t('features.v2.info.strong')}</strong>
                {' '}{t('features.v2.info.body')}
              </div>
            </div>
          </Reveal>

          {/* TIER-GRID */}
          <div className="grid md:grid-cols-2 gap-6 mb-14">
            {TIERS.map((tier) => {
              const inherits = tier.key !== 'public' ? t(`${tier.tierKey}.inherits`) : null
              return (
                <div
                  key={tier.key}
                  className="wertung-flight-card rounded-[20px] p-6 md:p-8 flex flex-col gap-5"
                  style={{ border: `1.5px solid ${tier.borderColor}` }}
                >
                  <div>
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <h2 className="text-xl md:text-2xl font-black uppercase tracking-tight">
                        {t(`${tier.tierKey}.name`)}
                      </h2>
                      <span
                        className="wertung-flight-pill px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase"
                        style={{ ['--pill-color' as any]: tier.badgeText }}
                      >
                        {t(`${tier.tierKey}.badge`)}
                      </span>
                    </div>
                    <p className="text-sm font-semibold" style={{ color: tier.accentColor }}>
                      {t(`${tier.tierKey}.headline`)}
                    </p>
                  </div>

                  <div
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] leading-snug"
                    style={{ background: tier.accentBg, color: tier.accentColor }}
                  >
                    <Rocket className="w-4 h-4 flex-shrink-0" />
                    {t(`${tier.tierKey}.range`)}
                  </div>

                  {inherits && (
                    <div className="flex items-center gap-2 text-xs italic" style={{ color: 'var(--muted)' }}>
                      <ChevronRight className="w-3.5 h-3.5" />
                      {inherits}
                    </div>
                  )}

                  <ul className="flex flex-col gap-2 flex-1">
                    {tier.features.map((feature) => {
                      const Icon = feature.icon
                      return (
                        <li key={feature.id}>
                          <button
                            type="button"
                            onClick={() => setModalState({ feature, tier })}
                            data-tip={t(`${feature.baseKey}.short`)}
                            data-tip-pos="bottom"
                            className="w-full flex items-start gap-3 text-left rounded-lg px-2 py-1.5 hover:bg-white/5 transition-colors"
                          >
                            <div
                              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                              style={{ background: tier.accentBg }}
                            >
                              <Icon className="w-3.5 h-3.5" style={{ color: tier.accentColor }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold leading-snug underline underline-offset-2 decoration-[var(--accent)]/40">
                                  {t(`${feature.baseKey}.title`)}
                                </span>
                                {feature.isNew && !feature.comingSoon && (
                                  <span
                                    className="wertung-flight-pill px-1.5 py-0 rounded-full text-[9px] font-black uppercase tracking-widest"
                                    style={{ ['--pill-color' as any]: '#f2c94e' }}
                                  >
                                    {t('features.v2.modal.newBadgeShort')}
                                  </span>
                                )}
                                {feature.comingSoon && (
                                  <span
                                    className="wertung-flight-pill px-1.5 py-0 rounded-full text-[9px] font-black uppercase tracking-widest"
                                    style={{ ['--pill-color' as any]: '#7e22ce' }}
                                  >
                                    {t('features.v2.modal.soonBadge', { when: t(`features.v2.times.${feature.comingSoon}`) })}
                                  </span>
                                )}
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 opacity-40 flex-shrink-0 mt-1" />
                          </button>
                        </li>
                      )
                    })}
                  </ul>

                  <div
                    className="text-[11px] font-mono uppercase tracking-wider pt-2 border-t"
                    style={{ color: 'var(--muted)', borderColor: 'var(--award-line)' }}
                  >
                    {t('features.v2.totalCount', { count: tier.features.length, tier: t(`${tier.tierKey}.name`) })}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Coaching App Section */}
          <section className="mb-14">
            <div
              className="rounded-[20px] p-6 md:p-8"
              style={{
                background: 'color-mix(in oklch, var(--award-ink-2) 92%, transparent)',
                backdropFilter: 'blur(6px)',
                border: '1.5px solid rgba(168,85,247,0.30)',
              }}
            >
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h2 className="text-xl md:text-2xl font-black uppercase tracking-tight">
                  {t('features.v2.app.title')}
                </h2>
                <span
                  className="wertung-flight-pill px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase"
                  style={{ ['--pill-color' as any]: '#7e22ce' }}
                >
                  {t('features.v2.app.badge')}
                </span>
              </div>
              <p className="text-sm mb-5 max-w-2xl" style={{ color: 'var(--muted)' }}>
                {t('features.v2.app.body')}
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { icon: RadioTower, tKey: 't1', tipKey: 'tip1' },
                  { icon: Activity, tKey: 't2', tipKey: 'tip2' },
                  { icon: BarChart, tKey: 't3', tipKey: 'tip3' },
                  { icon: Thermometer, tKey: 't4', tipKey: 'tip4' },
                  { icon: FileText, tKey: 't5', tipKey: 'tip5' },
                  { icon: Play, tKey: 't6', tipKey: 'tip6' },
                  { icon: TrendingUp, tKey: 't7', tipKey: 'tip7' },
                  { icon: TrendingUp, tKey: 't8', tipKey: 'tip8' },
                ].map((f, fi) => (
                  <div
                    key={fi}
                    className="wertung-flight-pill px-3 py-2.5 rounded-xl text-xs font-semibold flex items-center gap-2"
                    style={{ ['--pill-color' as any]: '#7e22ce' }}
                    data-tip={t(`features.v2.app.${f.tipKey}`)}
                    data-tip-pos="bottom"
                  >
                    <f.icon className="w-4 h-4 flex-shrink-0" />
                    <span>{t(`features.v2.app.${f.tKey}`)}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 mt-5 flex-wrap">
                <Link
                  href="/downloads"
                  className="wertung-tab-pill px-5 py-2 rounded-full text-[12px] font-mono font-bold uppercase tracking-[0.08em]"
                  data-state="active"
                >
                  {t('features.v2.app.ctaDownload')}
                </Link>
                <Link
                  href="/app/settings/devices"
                  className="wertung-tab-pill px-5 py-2 rounded-full text-[12px] font-mono font-bold uppercase tracking-[0.08em]"
                  data-state="inactive"
                >
                  {t('features.v2.app.ctaDevices')}
                </Link>
              </div>
            </div>
          </section>

          {/* Business Model */}
          <section className="mb-14">
            <div
              className="rounded-[20px] p-6 md:p-8 text-center"
              style={{
                background: 'var(--card)',
                border: '1.5px solid rgba(34,197,94,0.3)',
              }}
            >
              <div className="flex items-center justify-center gap-3 mb-3 flex-wrap">
                <h2 className="text-xl md:text-2xl font-black uppercase tracking-tight">
                  {t('features.v2.business.title')}
                </h2>
                <span
                  className="wertung-flight-pill px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase"
                  style={{ ['--pill-color' as any]: '#15803d' }}
                >
                  {t('features.v2.business.badge')}
                </span>
              </div>
              <p className="text-sm max-w-2xl mx-auto mb-4" style={{ color: 'var(--muted)' }}>
                {t('features.v2.business.body')}
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <div className="flex items-center gap-2 text-sm">
                  <Award className="w-4 h-4" style={{ color: '#15803d' }} />
                  <span>{t('features.v2.business.b1')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Award className="w-4 h-4" style={{ color: '#15803d' }} />
                  <span>{t('features.v2.business.b2')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4" style={{ color: '#15803d' }} />
                  <span>{t('features.v2.business.b3')}</span>
                </div>
              </div>
            </div>
          </section>

          {/* CTA-Closing */}
          <section className="wertung-flight-card rounded-[20px] text-center py-10 px-6">
            <h2 className="text-2xl font-black uppercase mb-3">{t('features.v2.cta.title')}</h2>
            <p className="text-sm mb-6 max-w-lg mx-auto" style={{ color: 'var(--muted)' }}>
              {t('features.v2.cta.body')}
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              {isLoggedIn ? (
                <Link
                  href="/app"
                  data-state="active"
                  className="wertung-tab-pill px-5 py-2 rounded-full text-[12px] font-mono font-bold uppercase tracking-[0.08em]"
                >
                  {t('features.v2.cta.dashboard')}
                </Link>
              ) : (
                <>
                  <Link
                    href="/register"
                    data-state="active"
                    className="wertung-tab-pill px-5 py-2 rounded-full text-[12px] font-mono font-bold uppercase tracking-[0.08em] flex items-center gap-2"
                  >
                    <UserPlus className="w-4 h-4" />
                    {t('features.v2.cta.register')}
                  </Link>
                  <Link
                    href="/login"
                    data-state="inactive"
                    className="wertung-tab-pill px-5 py-2 rounded-full text-[12px] font-mono font-bold uppercase tracking-[0.08em] flex items-center gap-2"
                  >
                    <LogIn className="w-4 h-4" />
                    {t('features.v2.cta.login')}
                  </Link>
                </>
              )}
            </div>
          </section>
        </main>

        {modalState && (
          <FeatureModal
            state={modalState}
            onClose={() => setModalState(null)}
            isLoggedIn={isLoggedIn}
            t={t}
          />
        )}
      </div>
    </LenisProvider>
  )
}
